// Runtime toggle for the visual that plays when an entity dies.
//
// "explode" is the original effect: the sprite shatters into a radial burst of
// spinning, reddening fragments that fly outward and fade in ~500ms.
//
// "crumble" is the new alternative: the same fragments instead fall under
// gravity, tumble, bounce and settle into a dusty pile at the entity's feet,
// with a small puff of dust rising off the base.
//
// Both live side by side so they can be compared in-game. The default stays on
// the original "explode". Flip the active style at runtime from the browser
// console:
//
//     deathEffect = "crumble"    // then kill an NPC to watch it crumble
//     deathEffect = "explode"    // back to the original
//
// or preload a style with a ?death=crumble query param on the game URL.

export type DeathEffectStyle = "explode" | "crumble"

const STYLES: DeathEffectStyle[] = ["explode", "crumble"]

const isDeathEffectStyle = (value: unknown): value is DeathEffectStyle =>
    typeof value === "string" && STYLES.includes(value as DeathEffectStyle)

let activeStyle: DeathEffectStyle = "crumble"

export const getDeathEffectStyle = (): DeathEffectStyle => activeStyle

export const setDeathEffectStyle = (style: DeathEffectStyle): void => {
    if (!isDeathEffectStyle(style)) {
        console.warn(`[deathEffect] unknown style ${JSON.stringify(style)}; expected one of ${STYLES.join(", ")}`)
        return
    }
    activeStyle = style
    console.log(`[deathEffect] active style: ${activeStyle}`)
}

// Wire up the browser-console toggle + optional ?death= query param. Guarded so
// it is a no-op in the Node test harness (no window).
if (typeof window !== "undefined") {
    try {
        const param = new URLSearchParams(window.location.search).get("death")
        if (isDeathEffectStyle(param)) {
            activeStyle = param
        }
    } catch {
        // window.location may be unavailable in some embeddings; ignore.
    }

    Object.defineProperty(window, "deathEffect", {
        get: () => activeStyle,
        set: (style: DeathEffectStyle) => setDeathEffectStyle(style),
        configurable: true,
    })
}
